<template>
  <section>

    <div class="limiter-content pa-2 pa-lg-0">

      <div class="padding-block"></div>

      <page-title v-if="dataset.data" eyebrow="CASOS / EMPRESAS" icon="mdi-briefcase-outline" :title="dataset.data.name" />

      <v-row justify="center">
        <v-col>
          <v-row align="center" justify="space-between">
            <v-col lg="7">
              <p><strong>Processo:</strong> {{ dataset.data.process_number || 'N/A' }}</p>
              <div v-if="clientCompanies.length" class="mb-3">
                <!-- Lista recolhida por padrao: o "+" abre, o "-" fecha -->
                <div class="company-group-header">
                  <strong>Empresas do Grupo ({{ clientCompanies.length }})</strong>
                  <v-btn icon small color="primary" class="company-group-toggle"
                    :aria-label="showCompanies ? 'Recolher empresas do grupo' : 'Expandir empresas do grupo'"
                    :aria-expanded="String(showCompanies)" @click="showCompanies = !showCompanies">
                    <v-icon small>{{ showCompanies ? "mdi-minus" : "mdi-plus" }}</v-icon>
                  </v-btn>
                </div>
                <v-expand-transition>
                <div v-show="showCompanies" class="company-group-list">
                  <div v-for="company in clientCompanies" :key="company.id || company.name" class="company-group-item">
                    <span>{{ company.name }}</span>
                    <small v-if="company.registration">{{ formatRegistration(company.registration) }}</small>
                  </div>
                </div>
                </v-expand-transition>
                <p v-if="consolidationText" class="mb-1 mt-2"><strong>Consolidação:</strong> {{ consolidationText }}</p>
              </div>
              <p><strong>Especialista Responsável Jurídico:</strong> {{ dataset.data.employee ? dataset.data.employee.name :
                'N/A' }} </p>
              <p v-if="dataset.data.accountingEmployee"><strong>Especialista Responsável Contabilidade:</strong> {{ dataset.data.accountingEmployee.name }}</p>
              <p v-if="dataset.data.judgment_at && dataset.data.judgment_at !== '0000-00-00 00:00:00'"><strong>Ajuizamento:</strong>
                {{ $moment(dataset.data.judgment_at).utc(false).format('DD/MM/YYYY') }}
              </p>
              <!-- <p><strong>Termo Legal:</strong>
                {{ dataset.data.legal_term && dataset.data.legal_term !== '0000-00-00 00:00:00' ?
                  $moment(dataset.data.legal_term).utc(false).format('DD/MM/YYYY') :
                'N/A' }}
              </p> -->
              <p v-if="dataset.data.legal_term && dataset.data.legal_term !== '0000-00-00 00:00:00'"><strong>Termo
                  Legal:</strong>
                {{ $moment(dataset.data.legal_term).utc(false).format('DD/MM/YYYY') }}
              </p>
              <!-- Falencia nao tem deferimento: casos antigos podem ter o
                   valor gravado, mas ele nao deve ser exibido. -->
              <p v-if="Number(dataset.data.type) !== 2 && dataset.data.deferment_at && dataset.data.deferment_at !== '0000-00-00 00:00:00'">
                <strong>Deferimento:</strong>
                {{ $moment(dataset.data.deferment_at).utc(false).format('DD/MM/YYYY') }}
              </p>
              <p v-if="dataset.data.insolvency_at && dataset.data.insolvency_at !== '0000-00-00 00:00:00'">
                <strong>Declaração da Insolvência:</strong>
                {{ $moment(dataset.data.insolvency_at).utc(false).format('DD/MM/YYYY') }}
              </p>
              <p v-if="dataset.data.liquidation_at && dataset.data.liquidation_at !== '0000-00-00 00:00:00'">
                <strong>Decretação da Quebra/Liquidação:</strong>
                {{ $moment(dataset.data.liquidation_at).utc(false).format('DD/MM/YYYY') }}
              </p>
              <p><strong>Vara:</strong> {{ dataset.data.local || 'N/A' }}</p>
              <p><strong>Comarca:</strong> {{ dataset.data.district ? dataset.data.district.name : 'N/A' }}</p>
            </v-col>

            <v-col lg="4">
              <v-img :src="dataset.data.logo_url"></v-img>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row no-gutters justify="center">
        <v-col>
          <div v-html="dataset.data.description"></div>
        </v-col>
      </v-row>

      <!-- Atalho para o credor enviar os dados bancarios ja com este caso selecionado -->
      <v-row v-if="canSendBankInformation" no-gutters class="mt-4">
        <v-col>
          <v-btn color="primary" depressed class="case-bank-btn"
            :to="{ path: '/informacao-bancaria', query: { client_id: dataset.data.id } }">
            <v-icon left small>mdi-bank-outline</v-icon>Enviar dados bancários
          </v-btn>
        </v-col>
      </v-row>

      <!-- Tabs com QGC, Timeline e Documentos -->
      <v-row justify="center">
        <v-col>
          <div class="case-tabs-wrapper">
            <!-- Tabs visíveis -->
            <div class="case-tabs-row">
              <button
                v-for="tab in visibleTabs"
                :key="tab.id"
                class="case-tab"
                :class="{ 'case-tab--active': activeTab === tab.id }"
                @click="activeTab = tab.id"
              >
                {{ tab.name }}
              </button>

              <!-- Botão "mais" com popover para tabs ocultas -->
              <v-menu v-if="hiddenTabs.length > 0" offset-y bottom left>
                <template #activator="{ on, attrs }">
                  <button class="case-tab case-tab--more" v-bind="attrs" v-on="on">
                    <v-icon small>mdi-chevron-down</v-icon>
                    Mais ({{ hiddenTabs.length }})
                  </button>
                </template>
                <v-list dense class="case-tabs-popover">
                  <v-list-item
                    v-for="tab in hiddenTabs"
                    :key="tab.id"
                    @click="activeTab = tab.id"
                    :class="{ 'v-list-item--active': activeTab === tab.id }"
                  >
                    <v-list-item-title>{{ tab.name }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <!-- Conteúdo da tab ativa -->
            <div class="case-tab-content">

              <!-- QGC -->
              <div v-if="activeTab === 'qgc' && isQgcVisible">
                <creditors-list v-if="dataset.data.id" :clientId="dataset.data.id" />
              </div>

              <!-- Cronograma Processual -->
              <div v-else-if="activeTab === 'timeline'">
                <div v-if="timelineLoading" class="text-center py-8">
                  <v-progress-circular indeterminate size="32" width="2" color="primary"></v-progress-circular>
                </div>
                <div v-else-if="timelineEvents.length > 0">
                  <h3 class="tl-section-title">Cronograma Processual</h3>
                  <div class="tl-container">
                    <div class="tl-scroll">
                      <!-- Linha superior (eventos pares: título + ref) -->
                      <div class="tl-row tl-row--top">
                        <div v-for="(event, idx) in timelineEvents" :key="'top-'+event.id" class="tl-cell">
                          <template v-if="idx % 2 === 0">
                            <div class="tl-label">
                              <div class="tl-label-title">{{ event.title }}</div>
                              <div v-if="event.article_ref" class="tl-label-ref">({{ event.article_ref }})</div>
                            </div>
                            <div class="tl-connector tl-connector--down"></div>
                          </template>
                        </div>
                      </div>

                      <!-- Linha do meio (dots + linha) -->
                      <div class="tl-row tl-row--middle">
                        <div class="tl-line"></div>
                        <div v-for="event in timelineEvents" :key="'dot-'+event.id" class="tl-cell">
                          <div class="tl-dot" :class="{ 'tl-dot--done': event.date, 'tl-dot--pending': !event.date }"></div>
                        </div>
                      </div>

                      <!-- Linha de datas -->
                      <div class="tl-row tl-row--dates">
                        <div v-for="event in timelineEvents" :key="'date-'+event.id" class="tl-cell">
                          <div class="tl-date" :class="{ 'tl-date--pending': !event.date }">
                            {{ event.date ? $moment(event.date).format('DD/MM/YYYY') : 'Sem previsão' }}
                          </div>
                        </div>
                      </div>

                      <!-- Linha inferior (eventos ímpares: título + ref) -->
                      <div class="tl-row tl-row--bottom">
                        <div v-for="(event, idx) in timelineEvents" :key="'bot-'+event.id" class="tl-cell">
                          <template v-if="idx % 2 !== 0">
                            <div class="tl-connector tl-connector--up"></div>
                            <div class="tl-label">
                              <div class="tl-label-title">{{ event.title }}</div>
                              <div v-if="event.article_ref" class="tl-label-ref">({{ event.article_ref }})</div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-tab-message">
                  Cronograma processual não disponível para este caso.
                </div>
              </div>

              <!-- Noticias do caso (cadastradas no backoffice) -->
              <div v-else-if="activeTab === 'news'">
                <case-news v-if="dataset.data.id" :client-id="dataset.data.id" />
              </div>

              <!-- Abas de documentos -->
              <div v-else>
                <v-list v-if="activeMainDocuments.length || !activeDocuments.length" class="gray">
                  <template v-if="activeDocuments.length > 0">
                    <a
                      v-for="(item, index) in activeMainDocuments"
                      :key="index"
                      :href="item.doc_url"
                      target="_blank"
                      class="d-flex align-center list-item-link mb-2 white"
                    >
                      <v-list-item class="d-flex align-center">
                        <v-list-item-avatar>
                          <v-img src="@/assets/logo-bej-small.png"></v-img>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium mediumGray--text">
                            {{ item.name }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </a>
                  </template>
                  <template v-else>
                    <div class="empty-tab-message">Nenhum documento adicionado nesta aba</div>
                  </template>
                </v-list>

                <!-- Subcategorias da aba: titulo e documentos, uma abaixo da outra -->
                <div v-for="section in activeDocumentSections" :key="section.name" class="doc-section mt-6">
                  <div class="doc-section__title">
                    <v-icon small class="mr-2">mdi-folder-outline</v-icon>{{ section.name }}
                    <span class="doc-section__count">{{ section.documents.length }} documento(s)</span>
                  </div>
                  <v-list class="gray">
                    <a
                      v-for="(item, index) in section.documents"
                      :key="index"
                      :href="item.doc_url"
                      target="_blank"
                      class="d-flex align-center list-item-link mb-2 white"
                    >
                      <v-list-item class="d-flex align-center">
                        <v-list-item-avatar>
                          <v-img src="@/assets/logo-bej-small.png"></v-img>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium mediumGray--text">
                            {{ item.name }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </a>
                  </v-list>
                </div>
              </div>

            </div>
          </div>
        </v-col>
      </v-row>

      <v-dialog v-if="dataset.data.banner_url" v-model="dialog" max-width="600" hide-overlay
        @click:outside="closeDialog = false">
        <v-card>
          <v-img :src="dataset.data.banner_url"></v-img>
          <v-card-actions>
            <v-btn @click="closeDialog" color="primary">Fechar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-row>
        <whatsButton />
      </v-row>

      <v-row>
        <topButton />
      </v-row>

    </div>

  </section>
</template>

<script>
import 'moment/locale/pt-br';
import axios from "axios";
import topButton from '@/components/layout/GoToTopButton.vue';
import whatsButton from '@/components/layout/WhatsAppButton.vue';
import CreditorsList from '@/components/cases/CreditorsList.vue';
import CaseNews from '@/components/cases/CaseNews.vue';
import clientDocTypes from '@/common/data/client-doc-types.data';

export default {
  name: 'CasesDetails',
  components: {
    topButton,
    whatsButton,
    CreditorsList,
    CaseNews,
  },

  data() {
    return {
      dataset: {
        data: {},
        clientDocTypes: [],
      },
      dialog: false,
      showCompanies: false,
      activeTab: 'qgc',
      maxVisibleTabs: 5,
      timelineEvents: [],
      timelineLoading: false,
    }
  },

  computed: {
    allTabs() {
      const docTabs = this.dataset.clientDocTypes.map(dt => ({
        id: `doc-${dt.key}`,
        name: dt.name,
        docCategoryId: dt.id,
      }));
      const tabs = [
        { id: 'timeline', name: 'Cronograma Processual' },
        { id: 'news', name: 'Notícias' },
        ...docTabs,
      ];

      if (this.isQgcVisible) {
        const primaryDocumentsIndex = tabs.findIndex(tab =>
          this.normalizeText(tab.name) === 'principais documentos'
        );
        const qgcIndex = primaryDocumentsIndex >= 0 ? primaryDocumentsIndex + 1 : 1;

        tabs.splice(qgcIndex, 0, { id: 'qgc', name: 'Quadro Geral de Credores' });
      }

      return tabs;
    },

    visibleTabs() {
      return this.allTabs.slice(0, this.maxVisibleTabs);
    },

    hiddenTabs() {
      return this.allTabs.slice(this.maxVisibleTabs);
    },

    activeDocuments() {
      if (!this.activeTab.startsWith('doc-')) return [];
      const key = this.activeTab.replace('doc-', '');
      const docType = this.dataset.clientDocTypes.find(dt => String(dt.key) === key);
      return docType && docType.documents ? docType.documents : [];
    },

    // Documentos sem subdivisao (lista principal da aba).
    activeMainDocuments() {
      return this.activeDocuments.filter((doc) => !doc.section);
    },

    // Subdivisoes da aba, na ordem em que aparecem.
    activeDocumentSections() {
      const groups = [];
      this.activeDocuments.filter((doc) => doc.section).forEach((doc) => {
        let group = groups.find((item) => item.name === doc.section);
        if (!group) {
          group = { name: doc.section, documents: [] };
          groups.push(group);
        }
        group.documents.push(doc);
      });
      return groups;
    },

    clientCompanies() {
      return Array.isArray(this.dataset.data.companies) ? this.dataset.data.companies : [];
    },

    // Regime cadastrado no caso (1 processual, 2 substancial).
    consolidationText() {
      const type = Number(this.dataset.data.consolidation_type);
      if (type === 2) return 'Substancial (art. 69-J da Lei 11.101/2005)';
      if (type === 1) return 'Processual (art. 69-G da Lei 11.101/2005)';
      return null;
    },

    isQgcVisible() {
      return Number(this.dataset.data.show_qgc_on_site) === 1;
    },

    // Dados bancarios sao enviados pela conta do credor (a pagina nao atende procurador,
    // como no menu). Visitante sem login ve o botao e a pagina pede o login.
    canSendBankInformation() {
      const user = this.$root.user;
      const isProcurator = !!user && Number(user.type) === 2;
      const data = this.dataset.data || {};
      const bankAllowed = data.portal_bank_enabled === undefined
        ? (data.creditor_portal_enabled === undefined || Number(data.creditor_portal_enabled) !== 0)
        : Number(data.portal_bank_enabled) !== 0;
      return !!data.id && !isProcurator && bankAllowed;
    },
  },

  watch: {
    activeTab(val) {
      if (val === 'timeline' && this.timelineEvents.length === 0) {
        this.loadTimeline();
      }
    },
  },

  created() {
    this.dialog = true;
  },

  mounted() {
    window.scrollTo(0, 0)
    this.load();
  },

  methods: {

    formatRegistration(value) {
      const digits = String(value || '').replace(/\D/g, '');
      if (digits.length !== 14) return value;
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    },

    async loadTimeline() {
      if (!this.dataset.data.id) return;
      this.timelineLoading = true;
      try {
        const { data } = await axios.get(`/api/clients/${this.dataset.data.id}/events`);
        this.timelineEvents = data.content || [];
      } catch (error) {
        this.timelineEvents = [];
      } finally {
        this.timelineLoading = false;
      }
    },

    async load() {
      try {
        const result = await axios.get(`/api/clients/${this.$route.params.id}`);
        this.dataset.data = result.data.content;

        this.dataset.clientDocTypes = this.buildDocumentTabs(this.dataset.data);
        if (!this.isQgcVisible && this.activeTab === 'qgc') {
          this.activeTab = 'timeline';
        }

      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 2000
        })
      }
    },

    buildDocumentTabs(data) {
      const documents = Array.isArray(data.documents) ? data.documents : [];
      const categories = Array.isArray(data.doc_categories)
        ? this.ensureCreditVerificationReportCategory(data.doc_categories)
        : [];
      const usedDocumentIds = new Set();

      const tabs = categories.map((category) => {
        const docs = documents
          .filter((doc) => {
            const isCategoryDoc = category.id && doc.client_doc_category_id === category.id;
            const isLegacyTypeDoc = !doc.client_doc_category_id && category.type && doc.type === category.type;

            if (isCategoryDoc || isLegacyTypeDoc) {
              usedDocumentIds.add(doc.id);
              return true;
            }

            return false;
          })
          .sort((a, b) => a.order - b.order);

        return {
          ...category,
          key: `category-${category.id}`,
          documents: docs,
        };
      });

      const orphanLegacyDocs = documents
        .filter((doc) => !usedDocumentIds.has(doc.id))
        .sort((a, b) => a.order - b.order);

      orphanLegacyDocs.forEach((doc) => {
        const key = `legacy-${doc.type || 'uncategorized'}`;
        let tab = tabs.find((item) => item.key === key);

        if (!tab) {
          tab = {
            id: null,
            key,
            name: this.getLegacyDocumentTypeName(doc.type),
            type: doc.type || null,
            documents: [],
          };
          tabs.push(tab);
        }

        tab.documents.push(doc);
      });

      return this.sortDocumentTabs(tabs);
    },

    ensureCreditVerificationReportCategory(categories) {
      const normalizedCategories = categories.slice();
      const hasCreditVerificationReport = normalizedCategories.some((category) => {
        return category.type === 6 || this.isCreditVerificationReportCategory(category.name);
      });

      if (!hasCreditVerificationReport) {
        const fixedCategory = clientDocTypes.find((item) => item.id === 6);

        if (fixedCategory) {
          normalizedCategories.push({
            id: null,
            key: 'legacy-6',
            name: fixedCategory.name,
            type: fixedCategory.id,
            is_legacy: true,
          });
        }
      }

      return normalizedCategories;
    },

    isCreditVerificationReportCategory(name) {
      const normalized = this.normalizeText(name);
      return normalized.includes('relatorio') &&
        normalized.includes('verificacao de creditos');
    },

    sortDocumentTabs(tabs) {
      return tabs.slice().sort((a, b) => {
        const aIsOther = this.isOtherDocumentsCategory(a.name);
        const bIsOther = this.isOtherDocumentsCategory(b.name);

        if (aIsOther === bIsOther) return 0;
        return aIsOther ? 1 : -1;
      });
    },

    isOtherDocumentsCategory(name) {
      const normalized = this.normalizeText(name);
      return normalized === 'outros' || normalized === 'outros documentos';
    },

    normalizeText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    },

    getLegacyDocumentTypeName(type) {
      const legacyTypes = {
        1: 'Principais Documentos',
        2: 'Relatório de Atividades',
        3: 'Atas',
        4: 'Relatório de Incidentes',
        5: 'Relatório de Ofícios',
        6: 'Relatório de Verificação de Créditos',
      };

      return legacyTypes[type] || 'Outros Documentos';
    },

    closeDialog() {
      this.dialog = false;
    }
  }
}
</script>

<style>
/* Empresas do grupo: cabecalho com o botao de abrir/fechar e lista com o CNPJ separado do nome */
.company-group-header {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.company-group-toggle {
  margin-left: -6px;
}



.company-group-list {
  margin-top: 6px;
  padding-left: 12px;
  border-left: 2px solid rgba(126, 85, 171, 0.25);
}

.company-group-item {
  padding: 3px 0;
}

.company-group-item small {
  margin-left: 8px;
  color: #73677c;
  white-space: nowrap;
}

.list-item-link {
  text-decoration: none;
  font-family: "Raleway" !important;
}

/* Custom tabs */
.case-tabs-wrapper {
  margin-top: 16px;
}

.case-tabs-row {
  display: flex;
  align-items: center;
  gap: 0;
  border-bottom: 2px solid #EFEFEF;
  overflow: visible;
}

.case-tab {
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #525252;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  font-family: "Raleway", sans-serif;
}

.case-tab:hover {
  color: #180032;
  background-color: #F7F7F7;
}

.case-tab--active {
  color: #180032;
  font-weight: 700;
  border-bottom-color: #180032;
}

.case-tab--more {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #7877e5;
  font-weight: 600;
  margin-left: auto;
}

.case-tab--more:hover {
  color: #180032;
}

.case-tabs-popover .v-list-item {
  min-height: 40px;
  font-size: 14px;
}

.case-tabs-popover .v-list-item--active {
  background-color: #F3F0FF;
}

.case-tabs-popover .v-list-item--active .v-list-item__title {
  color: #180032;
  font-weight: 700;
}

.case-tab-content {
  padding-top: 24px;
}

.empty-tab-message {
  padding: 40px 16px;
  text-align: center;
  color: #9E9E9E;
  font-size: 14px;
}

/* Subcategorias da aba de documentos. */
.doc-section__title {
  display: flex;
  align-items: center;
  padding: 0 4px 10px;
  margin-bottom: 8px;
  border-bottom: 2px solid #e6e2ee;
  font-size: 16px;
  font-weight: 700;
  color: #1d0533;
}

.doc-section__count {
  margin-left: auto;
  font-weight: 400;
  font-size: 13px;
  color: #6b6880;
}

/* Timeline */
.case-timeline {
  padding: 8px 0;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 0;
  border-left: 2px solid #EFEFEF;
  margin-left: 8px;
  padding-left: 24px;
  position: relative;
}

.timeline-dot {
  position: absolute;
  left: -6px;
  top: 20px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #7877e5;
  border: 2px solid #FFFFFF;
}

.timeline-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.timeline-date {
  font-size: 12px;
  color: #9E9E9E;
  font-weight: 500;
}

.timeline-title {
  font-size: 14px;
  font-weight: 600;
  color: #180032;
}

.timeline-desc {
  font-size: 13px;
  color: #525252;
  margin-top: 2px;
}

/* Cronograma Processual */
.tl-section-title {
  font-size: 20px;
  font-weight: 700;
  color: #180032;
  margin-bottom: 24px;
}

.tl-container {
  background: #FAFAFA;
  border: 1px solid #EFEFEF;
  border-radius: 10px;
  padding: 32px 24px;
  overflow-x: auto;
}

.tl-scroll {
  min-width: max-content;
}

.tl-row {
  display: flex;
}

.tl-cell {
  flex: 1 0 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 140px;
}

/* Linha do meio com dots */
.tl-row--middle {
  position: relative;
  height: 40px;
  align-items: center;
}

.tl-line {
  position: absolute;
  top: 50%;
  left: 40px;
  right: 40px;
  height: 3px;
  background: linear-gradient(90deg, #180032 0%, #7877e5 50%, #CFCFCF 100%);
  transform: translateY(-50%);
  border-radius: 2px;
}

.tl-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  z-index: 2;
  border: 3px solid #FFFFFF;
  box-shadow: 0 0 0 2px #180032;
  background: #180032;
}

.tl-dot--pending {
  background: #FFFFFF;
  box-shadow: 0 0 0 2px #CFCFCF;
}

/* Labels superiores (eventos pares) */
.tl-row--top {
  min-height: 80px;
  align-items: flex-end;
  padding-bottom: 4px;
}

.tl-row--top .tl-label {
  margin-bottom: 4px;
}

/* Labels inferiores (eventos ímpares) */
.tl-row--bottom {
  min-height: 80px;
  align-items: flex-start;
  padding-top: 4px;
}

.tl-row--bottom .tl-label {
  margin-top: 4px;
}

/* Conectores verticais */
.tl-connector {
  width: 1px;
  height: 16px;
  background: #C0C0C0;
}

.tl-label {
  text-align: center;
  max-width: 130px;
}

.tl-label-title {
  font-size: 12px;
  font-weight: 600;
  color: #180032;
  line-height: 1.3;
}

.tl-label-ref {
  font-size: 10px;
  color: #9E9E9E;
  margin-top: 1px;
}

/* Datas */
.tl-row--dates {
  margin-top: 4px;
  min-height: 24px;
}

.tl-date {
  font-size: 12px;
  font-weight: 700;
  color: #180032;
}

.tl-date--pending {
  color: #9E9E9E;
  font-weight: 500;
  font-style: italic;
  font-size: 11px;
}
</style>
